/**
 * TrendsSection - Top section showing related market/industry trends
 * Question: "What forces are driving or amplifying this issue?"
 */

import React from 'react';
import { TrendingUp, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { RelatedItemCard, NODE_COLORS } from './RelatedItemCard';
import type { RelatedTrend } from './hooks/useIssueRelationships';

interface TrendsSectionProps {
  trends: RelatedTrend[];
  onTrendClick?: (trendId: string) => void;
}

// Time horizon badge styles
const TIME_HORIZON_STYLES: Record<string, string> = {
  'short-term': 'bg-green-100 text-green-700',
  'medium-term': 'bg-yellow-100 text-yellow-700',
  'long-term': 'bg-blue-100 text-blue-700',
};

export const TrendsSection: React.FC<TrendsSectionProps> = ({
  trends,
  onTrendClick,
}) => {
  const colors = NODE_COLORS.Trend;

  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} overflow-hidden`}>
      {/* Header */}
      <div className="px-4 py-3 bg-white/60 border-b border-blue-100">
        <div className="flex items-center gap-2">
          <TrendingUp className={`w-5 h-5 ${colors.accent}`} />
          <h3 className={`font-semibold ${colors.accent}`}>Context: Market Trends</h3>
        </div>
        <p className="text-xs text-neutral-500 mt-1">
          What forces are driving or amplifying this issue?
        </p>
      </div>

      {/* Content */}
      <div className="p-4">
        {trends.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-wrap gap-3">
            {trends.map((item) => (
              <div
                key={item.trend.trend_id}
                className="flex-shrink-0 w-full sm:w-auto sm:flex-1 sm:min-w-[250px] sm:max-w-[300px]"
              >
                <RelatedItemCard
                  title={item.trend.title}
                  description={item.trend.description}
                  type="Trend"
                  relationship={item.relationship}
                  accentColor={colors.accent}
                  borderColor={colors.border}
                  bgColor="bg-white"
                >
                  <div className="space-y-2">
                    {/* Impact */}
                    {item.trend.impact && (
                      <div className="flex items-start gap-2">
                        <ArrowRight className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-neutral-600">
                          <span className="font-medium">Impact:</span> {item.trend.impact}
                        </span>
                      </div>
                    )}

                    {/* Time Horizon */}
                    {item.trend.time_horizon && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-neutral-400" />
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            TIME_HORIZON_STYLES[item.trend.time_horizon] ||
                            'bg-neutral-100 text-neutral-600'
                          }`}
                        >
                          {item.trend.time_horizon}
                        </span>
                      </div>
                    )}

                    {/* Relevance Score */}
                    {item.trend.relevance_score != null && (
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-yellow-500" />
                        <span className="text-xs text-neutral-600">
                          Relevance: {Math.round(item.trend.relevance_score * 100)}%
                        </span>
                      </div>
                    )}
                  </div>
                </RelatedItemCard>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="text-center py-6">
    <TrendingUp className="w-10 h-10 text-blue-200 mx-auto mb-2" />
    <p className="text-sm text-neutral-500">No related trends discovered yet</p>
    <p className="text-xs text-neutral-400 mt-1">
      Run relationship discovery to find connected trends
    </p>
  </div>
);

export default TrendsSection;
